import { selectFireById } from "features/entity-caching/fires/selectors"
import { selectWorkspaceMembers } from "features/entity-caching/members/UserManagementSlice"
import { matchesMemberQuery } from "features/overview/search/registry/plugins/matchesMemberQuery"
import { MembersSort } from "features/overview/slice"
import {
    orgUserLocationStore,
    useOrgUsersSharingLocationKey,
} from "features/user-locations/org-user-location-store"
import { RootState } from "integrations/redux"
import { useAppSelector } from "integrations/redux/hooks"
import { UserLocation } from "@nova/schemas"
import { useMemo } from "react"

export type SharingMember = {
    userId: string
    fullName: string
    email: string
    location: UserLocation
    projectName: string | undefined
}

export const getSharingMembers = ({
    state,
    locations,
    query = "",
    sort,
}: {
    state: RootState
    locations: ReadonlyMap<string, UserLocation>
    query?: string
    sort: MembersSort
}) => {
    const q = query.trim().toLowerCase()
    const directionMultiplier = sort.direction === "ascending" ? 1 : -1
    return selectWorkspaceMembers(state)
        .flatMap((member) => {
            const location = locations.get(member.userId)
            if (!location || location.expiresAt <= Date.now()) return []
            if (!matchesMemberQuery(member, q)) return []
            return [
                {
                    userId: member.userId,
                    fullName: member.fullName,
                    email: member.email,
                    location,
                    projectName: selectFireById(location.projectId)(state)?.name,
                },
            ]
        })
        .sort(
            (a, b) =>
                directionMultiplier *
                (sort.by === "alphabetical"
                    ? a.fullName.localeCompare(b.fullName)
                    : a.location.expiresAt - b.location.expiresAt)
        )
}

export const useSharingMemberCount = () => {
    const members = useAppSelector(selectWorkspaceMembers)
    const sharingUserIdsKey = useOrgUsersSharingLocationKey()
    return useMemo(() => {
        const locations = orgUserLocationStore.get()
        const sharingUserIds = new Set(sharingUserIdsKey ? sharingUserIdsKey.split(",") : [])
        return members.filter(
            (member) =>
                sharingUserIds.has(member.userId) &&
                (locations.get(member.userId)?.expiresAt ?? 0) > Date.now()
        ).length
    }, [members, sharingUserIdsKey])
}
